import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

/**
 * Star button that pins/unpins a piece of equipment for the current user.
 *
 * Props:
 *   machineryId   — required, the equipment id to toggle
 *   pinned        — current pin state (controlled by parent)
 *   onChange      — (newPinnedState, newIdsArray) => void; parent updates cache
 *   size          — 'sm' | 'md' — visual size, default 'md'
 *   className     — extra classes for positioning
 *
 * Click stops propagation so it's safe to embed inside a clickable card.
 */
const PinButton = ({ machineryId, pinned, onChange, size = 'md', className = '' }) => {
  const [busy, setBusy] = useState(false);
  const dims = size === 'sm' ? 'h-6 w-6' : 'h-8 w-8';
  const iconSize = size === 'sm' ? 14 : 18;

  const toggle = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/equipment-pins/${machineryId}`);
      const nowPinned = res.data.action === 'pinned';
      onChange?.(nowPinned, res.data.pinned_equipment_ids || []);
      toast.success(nowPinned ? 'Pinned to Dashboard' : 'Removed from pins');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update pin');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-label={pinned ? 'Unpin from Dashboard' : 'Pin to Dashboard'}
      title={pinned ? 'Unpin from Dashboard' : 'Pin to Dashboard'}
      data-testid={`pin-btn-${machineryId}`}
      data-pinned={pinned ? 'true' : 'false'}
      className={`${dims} inline-flex items-center justify-center rounded-full transition ${
        pinned
          ? 'text-amber-500 hover:bg-amber-50'
          : 'text-slate-400 hover:text-amber-500 hover:bg-slate-100'
      } ${busy ? 'opacity-50 cursor-wait' : 'cursor-pointer'} ${className}`}
    >
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill={pinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    </button>
  );
};

export default PinButton;
